import { FC } from "react";
import { useTheme } from "~/hooks/useTheme";

interface ReadingViewProps {
  documentUrl?: string;
}

export const ReadingView: FC<ReadingViewProps> = ({ documentUrl }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== "light";

  return (
    <div
      aria-hidden="true"
      style={{
        flex: 1,
        height: "100%",
        overflow: "hidden",
        borderRadius: 8,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "hsl(var(--border))"}`,
        background: isDark ? "#0f0f11" : "hsl(var(--muted))",
        padding: 0,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "stretch",
        position: "relative",
      }}
    >
      {documentUrl ? (
        <iframe
          src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          title="Uploaded report"
          style={{
            flex: 1,
            width: "100%",
            height: "100%",
            border: "none",
            background: "#fff",
            borderRadius: 8,
          }}
        />
      ) : (
        <DocumentPlaceholder />
      )}

      {/* Overlay — pointer-events:none so it doesn't block scroll */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {/* Subtle blue tint */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: isDark
              ? "rgba(37,99,235,0.06)"
              : "rgba(37,99,235,0.04)",
            mixBlendMode: isDark ? "screen" : "multiply",
          }}
        />
        {/* Scan beam */}
        <div
          className="cascade-animate"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 120,
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(37,99,235,0.10) 30%, rgba(37,99,235,0.32) 50%, rgba(37,99,235,0.10) 70%, transparent 100%)",
            animation: "cascade-scan-page 8s ease-in-out infinite",
          }}
        />
        {/* Sharp scan line */}
        <div
          className="cascade-animate"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 2,
            background: "rgba(37,99,235,0.85)",
            boxShadow: "0 0 12px 2px rgba(37,99,235,0.6)",
            animation: "cascade-scan-line 8s ease-in-out infinite",
          }}
        />
      </div>
    </div>
  );
};

/**
 * Skeleton placeholder when no document URL is available.
 * Renders faint bars resembling a text document.
 */
function DocumentPlaceholder() {
  const LINE_WIDTHS = [92, 88, 96, 84, 90, 94, 78, 86, 92, 88, 96, 72];
  return (
    <div
      style={{
        flex: 1,
        background: "#fff",
        borderRadius: 8,
        padding: "40px 48px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* Title skeleton */}
      <div
        style={{
          height: 12,
          width: "45%",
          background: "rgba(0,0,0,0.12)",
          borderRadius: 3,
          marginBottom: 8,
        }}
      />
      {LINE_WIDTHS.map((w, i) => (
        <div
          key={i}
          style={{
            height: 6,
            width: `${w}%`,
            background: "rgba(0,0,0,0.06)",
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  );
}
